import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React, { ReactNode } from 'react'

import { CrossIcon } from '@emico/icons'
import { overrideOptional } from '@emico/overrides'
import { theme, minWidth } from '@emico/styles'

import ButtonUnstyled from './ButtonUnstyled'
import Container from './Container'
import { H2 } from './Heading'

export interface BasicProps {
  children: ReactNode
  modalTitle?: string
  icon?: ReactNode
  hasContentPadding?: boolean
}

// Prevent `hasCloseButton` to be passed to component without `close`
export interface WithCloseProps extends BasicProps {
  hasCloseButton: true // Show a close button in modal body
  close: () => void // Close modal function
}

export interface WithoutCloseProps extends BasicProps {
  hasCloseButton?: false
}

type Props = WithCloseProps | WithoutCloseProps

export const Body = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'hasModalHeader' && prop !== 'hasCloseButton',
})<{
  hasModalHeader?: boolean
  hasCloseButton?: boolean
}>`
  position: relative;
  height: 100%;
  width: 100%;
  background-color: ${theme.backgroundColor};
  overflow-y: auto;

  @media ${minWidth('md')} {
    height: auto;
    max-height: 80vh;
  }

  ${(props) =>
    !props.hasModalHeader &&
    props.hasCloseButton &&
    css`
      padding-top: ${theme.spacing.x4}px;
    `}
`

export const ModalCloseButton = overrideOptional(
  styled(ButtonUnstyled, {
    shouldForwardProp: (prop) => prop !== 'hasModalHeader',
  })<{ hasModalHeader: boolean }>`
    position: absolute;
    right: ${theme.spacing.x2}px;
    top: ${theme.spacing.x2}px;
  `,
)

export const ModalCrossIcon = styled(CrossIcon)`
  color: ${theme.primaryColor};
`

export const ModalHeader = overrideOptional(styled.header`
  display: flex;
  align-items: center;
  margin-bottom: ${theme.spacing.x2}px;
  padding: ${theme.spacing.x2}px;

  svg {
    margin-right: ${theme.spacing.x2}px;
  }
`)

export const ModalTitle = styled(H2)`
  margin: 0;
  font-size: ${theme.fontSizeH3};
`

export const ModalContent = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasContentPadding',
})<{ hasContentPadding?: boolean }>`
  padding: ${({ hasContentPadding }) =>
    hasContentPadding ? theme.spacing.x2 : 0}px;
`

const ModalBody = (props: Props) => {
  const { children, modalTitle, icon, hasContentPadding = true } = props
  const hasModalHeader = Boolean(modalTitle || icon)

  return (
    <Container noPadding>
      <Body
        hasCloseButton={props.hasCloseButton}
        hasModalHeader={hasModalHeader}
        {...props}
      >
        {props.hasCloseButton && (
          <ModalCloseButton
            analyticsContext="active modal"
            analyticsName="close modal"
            onClick={props.close}
            hasModalHeader={hasModalHeader}
          >
            <ModalCrossIcon />
          </ModalCloseButton>
        )}

        {hasModalHeader && (
          <ModalHeader>
            {icon && icon}

            {modalTitle && <ModalTitle>{modalTitle}</ModalTitle>}
          </ModalHeader>
        )}

        <ModalContent hasContentPadding={hasContentPadding}>
          {children}
        </ModalContent>
      </Body>
    </Container>
  )
}

export default overrideOptional(ModalBody)
